'use client';

import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterSettingsFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsFragmentFragmentDoc';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';

import { useFeatureFlag } from '../../utils/featureFlags/useFeatureFlag';
import { filtersV2 } from '../../activeFeatureFlags';
import { useSearchFilters } from '../../hooks/useFilters';
import { FilterSettingsDesktop } from '../FilterSettingsDesktop/FilterSettingsDesktop';
import { SelectedFilterValueList } from '../SelectedFilterValueList/SelectedFilterValueList';
import { SelectedFilterValueListV2 } from '../SelectedFilterValueListV2/SelectedFilterValueListV2';
import { FilterSettingsMobile } from '../FilterSettingsMobile/FilterSettingsMobile.client';
import { FilterSettingsMobileV2 } from '../FilterSettingsMobileV2/FilterSettingsMobileV2.client';

/* GraphQL */ `
  fragment FilterSettingsFragment on SearchFilterableAndSortableResult {
    ...UseSearchFiltersFragment
    ...SelectedFilterValueListFragment
    ...SelectedFilterValueListV2Fragment
    ...FilterSettingsDesktopFragment
    ...FilterSettingsMobileFilterDialogButtonFragment
    ...FilterSettingsMobileFilterDialogButtonV2Fragment
  }
`;

const messages = defineMessages({
  eraseFiltersNotification: {
    id: 'eraseFilters.notification',
    defaultMessage:
      'Die ausgewählten Filter der vorherigen Seite wurden entfernt, um Dir Artikel anbieten zu können. Nutze bei Bedarf erneut die Filterauswahl.',
  },
});

export type RenderCategoryDialogContent = (closeDialog: () => void) => React.ReactNode;

export type FilterSettingsProps = {
  maskedData: FragmentType<typeof FilterSettingsFragmentFragmentDoc>;
  showRemovedFiltersNotification?: boolean;
  renderCategoryDialogContent?: RenderCategoryDialogContent;
};

/**
 * Responsive component to handle all filter interactions
 * */
export const FilterSettings = ({
  maskedData,
  showRemovedFiltersNotification,
  renderCategoryDialogContent,
}: FilterSettingsProps) => {
  const data = unmask<typeof FilterSettingsFragmentFragmentDoc>(maskedData);

  const isFiltersV2Enabled = useFeatureFlag(filtersV2);

  const { selectedFilterValues, pushFilterChange } = useSearchFilters(data);
  const { formatMessage } = useIntl();
  const { addNotification } = useNotification();

  useEffect(() => {
    if (showRemovedFiltersNotification) {
      addNotification({
        message: formatMessage(messages.eraseFiltersNotification),
        severity: 'warning',
        duration: 7000,
      });
      pushFilterChange([]);
    }
  }, [addNotification, formatMessage, pushFilterChange, showRemovedFiltersNotification]);

  return (
    <>
      <div className="hidden md:block">
        <FilterSettingsDesktop
          maskedData={data}
          selectedFilterValues={selectedFilterValues}
          onSubmit={pushFilterChange}
        />
      </div>
      <div className="md:hidden">
        {isFiltersV2Enabled ? (
          <FilterSettingsMobileV2
            maskedData={data}
            selectedFilterValues={selectedFilterValues}
            onSubmit={pushFilterChange}
            renderCategoryDialogContent={renderCategoryDialogContent}
          />
        ) : (
          <FilterSettingsMobile
            maskedData={data}
            selectedFilterValues={selectedFilterValues}
            onSubmit={pushFilterChange}
            renderCategoryDialogContent={renderCategoryDialogContent}
          />
        )}
      </div>

      {isFiltersV2Enabled ? (
        <div className="hidden md:block">
          <SelectedFilterValueListV2 maskedData={data} showColors />
        </div>
      ) : (
        <SelectedFilterValueList maskedData={data} showColors />
      )}
    </>
  );
};
